import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { getHebrewYear } from '../../../../../utils/hebrew-date-utils';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { CustomTag } from '../../../../../components/CustomTag/CustomTag';
import { CustomExpander } from '../../../../../components/CustomExpander/CustomExpander';
import './side-bar-filter.css';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { GeneralAttendanceSideBarFilterDataModel } from './general-attendance-side-bar-filter-data-model';
import {
  ProgramStudentStatus,
  getProgramStudentStatusDisplayText,
} from '../../../../../api/types/programs/students/program-student-status';
import { CourseStatus } from '../../../../../api/types/programs/course/course-status';

interface YearOption {
  year: string;
  code: string;
  value: number;
}

interface StatusOption {
  status: ProgramStudentStatus;
  severity: 'success' | 'warning' | 'danger' | 'info';
  checked: boolean;
}

export interface GeneralAttendanceSideBarProps {
  onSearch: (filters: GeneralAttendanceSideBarFilterDataModel) => any;
}

export const SideBarFilter: React.FC<GeneralAttendanceSideBarProps> = observer(
  ({ onSearch }) => {
    const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
    const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);
    const [selectedYears, setSelectedYears] = useState<number[]>([]);
    const [missedRatio, setMissedRatio] = useState<number | null>(null);
    const [missedRatioEnabled, setMissedRatioEnabled] =
      useState<boolean>(false);
    const [selectedCourseStatuses, setSelectedCourseStatuses] = useState<
      CourseStatus[]
    >([]);
    const [statuses, setStatuses] = useState<StatusOption[]>([
      {
        status: ProgramStudentStatus.STUDYING,
        severity: 'success',
        checked: false,
      },
      {
        status: ProgramStudentStatus.REGISTERED,
        severity: 'info',
        checked: false,
      },
      {
        status: ProgramStudentStatus.COMPLETED,
        severity: 'success',
        checked: false,
      },
      {
        status: ProgramStudentStatus.GRADUATED,
        severity: 'success',
        checked: false,
      },
      { status: ProgramStudentStatus.LEFT, severity: 'danger', checked: false },
      {
        status: ProgramStudentStatus.CANCELLED,
        severity: 'danger',
        checked: false,
      },
    ]);

    // Generate years array
    const years: YearOption[] = [];
    for (let year = 1980; year <= 2030; year++) {
      years.push({
        year: `${getHebrewYear(year)} (${year} - ${year + 1})`,
        code: 'year',
        value: year,
      });
    }

    const onStatusChange = (index: number) => {
      const newStatuses = [...statuses];
      newStatuses[index].checked = !newStatuses[index].checked;
      setStatuses(newStatuses);
    };

    const handleBranchToggle = (branchId: number) => {
      setSelectedBranches((prev) =>
        prev.includes(branchId)
          ? prev.filter((b) => b !== branchId)
          : [...prev, branchId],
      );
    };

    const handleProgramToggle = (programId: number) => {
      setSelectedPrograms((prev) =>
        prev.includes(programId)
          ? prev.filter((p) => p !== programId)
          : [...prev, programId],
      );
    };

    const handleYearToggle = (yearStr: string) => {
      const year = parseInt(yearStr);
      setSelectedYears((prev) =>
        prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year],
      );
    };

    const handleSearch = () => {
      const filters: GeneralAttendanceSideBarFilterDataModel = {
        branchesIds: selectedBranches,
        abstractProgramsIds: selectedPrograms,
        programsStartYears: selectedYears,
        programsStudentStatuses: statuses
          .filter((s) => s.checked)
          .map((s) => s.status),
        missedRatio: missedRatioEnabled ? missedRatio : null,
        courseStatuses: selectedCourseStatuses,
      };
      onSearch(filters);
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        marginBottom: '16px',
        color: '#333',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      statusContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px 0',
      },
      statusItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        direction: 'rtl' as const,
      },
      inputNumberContainer: {
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'center',
        gap: '8px',
        padding: '8px 0',
        width: '100%',
      },
      inputNumberWrapper: {
        flex: 1,
        minWidth: 0,
      },
      switchWrapper: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
      searchButton: {
        width: '100%',
        backgroundColor: '#2196F3',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>סינונים</div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranches}
            onItemToggle={handleBranchToggle}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="תכניות לימודים"
            items={globalSettingsStores.abstractProgram}
            selectedItems={selectedPrograms}
            onItemToggle={handleProgramToggle}
            getItemLabel={(program) => program.programName}
            getItemKey={(program) => program.id}
            searchPlaceholder="חפש תכנית..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שנים"
            items={years}
            selectedItems={selectedYears.map(String)}
            onItemToggle={handleYearToggle}
            getItemLabel={(yearOption) => yearOption.year}
            getItemKey={(yearOption) => yearOption.value.toString()}
            searchPlaceholder="חפש שנה..."
          />

          <CustomExpander badge={statuses.filter((s) => s.checked).length}>
            <div>סטטוס תלמיד בתוכנית</div>
            <div style={styles.statusContainer}>
              {statuses.map((status, index) => (
                <div key={status.status} style={styles.statusItem}>
                  <Checkbox
                    checked={status.checked}
                    onChange={() => onStatusChange(index)}
                  />
                  <CustomTag
                    width={'70px'}
                    text={getProgramStudentStatusDisplayText(status.status)}
                    severity={status.severity}
                  />
                </div>
              ))}
            </div>
          </CustomExpander>

          <CustomExpander>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span>אחוז היעדרות</span>
              <InputSwitch
                checked={missedRatioEnabled}
                onChange={(e) => setMissedRatioEnabled(e.value)}
                tooltip="כולל אחוז היעדרות"
                tooltipOptions={{ position: 'top' }}
              />
            </div>
            <div style={styles.inputNumberContainer}>
              <InputNumber
                value={missedRatio}
                onValueChange={(e) => setMissedRatio(e.value ?? null)}
                min={0}
                max={100}
                placeholder="הזן אחוז היעדרות"
                style={styles.inputNumberWrapper}
                suffix="%"
                disabled={!missedRatioEnabled}
              />
            </div>
          </CustomExpander>

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס קורס"
            items={
              Object.values(CourseStatus).filter(
                (value) => typeof value === 'string',
              ) as CourseStatus[]
            }
            selectedItems={selectedCourseStatuses}
            onItemToggle={(status: CourseStatus) => {
              setSelectedCourseStatuses((prev) =>
                prev.includes(status)
                  ? prev.filter((s) => s !== status)
                  : [...prev, status],
              );
            }}
            getItemLabel={(status) => CourseStatus.getDisplayText(status)}
            getItemKey={(status) => status}
            searchPlaceholder="חפש סטטוס..."
          />
        </div>
        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            style={styles.searchButton}
            onClick={handleSearch}
          />
        </div>
      </div>
    );
  },
);
